import { SendIcon } from 'lucide-react';
import { useRef } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import Button from '@/components/Button';

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  message: string;
  onMessageChange: (message: string) => void;
  className?: string;
  disabled?: boolean;
}

export function MessageInput({
  onSendMessage,
  message,
  onMessageChange,
  className,
  disabled = false,
}: MessageInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function sendMessage(): void {
    if (message.trim()) {
      onSendMessage(message.trim());
      onMessageChange('');
    }
  }

  function handleSubmit(e: React.SubmitEvent<HTMLFormElement>): void {
    e.preventDefault();
    sendMessage();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter') {
      return;
    }

    if (e.altKey) {
      e.preventDefault();
      const { selectionStart, selectionEnd } = e.currentTarget;
      e.currentTarget.setRangeText('\n', selectionStart, selectionEnd, 'end');
      onMessageChange(e.currentTarget.value);
      return;
    }

    if (!e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    onMessageChange(e.target.value);
  }

  function handleFormClick(e: React.MouseEvent) {
    if (textareaRef.current && e.target !== textareaRef.current) {
      const target = e.target as HTMLElement;
      const isButton = target.closest('button');
      if (!isButton) {
        textareaRef.current.focus();
      }
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      onClick={handleFormClick}
      className={`cursor-text rounded-lg border border-slate-300 bg-white px-3 py-2 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 ${className || ''}`}
    >
      <TextareaAutosize
        ref={textareaRef}
        value={message}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Talk to Covoy..."
        className="max-h-[25vh] w-full resize-none border-0 bg-transparent px-0 py-0 text-sm placeholder:text-slate-400 focus:outline-none"
        minRows={1}
      />
      <div className="mt-1 flex justify-end">
        <Button
          type="submit"
          variant="outline"
          size="icon"
          disabled={disabled || !message.trim()}
          aria-label="Send message"
          className="disabled:cursor-text"
        >
          <SendIcon />
        </Button>
      </div>
    </form>
  );
}
